import DEFAULT_I18N from "~/i18n"
import { PluginReviewActionNotAllowedError } from "~/lib/AjaxCtx"
import { ucfirst } from "~/lib/utils"

function flatQuestions({ sections, answers }) {
    const questions = []
    for (const section of sections) {
        for (const subsection of section.subsections) {
            for (const question of subsection.questions) {
                const answer = answers.find(a => a.questionId == question.id)
                questions.push({
                    questionId: question.id,
                    answer: answer ? answer.answer : null,
                    rules: question.rules,
                })
            }
        }
    }
    return questions
}

export function getRequiredQuestions({ sections, answers }) {
    const questions = flatQuestions({ sections, answers })

    return questions.filter(q => {
        const rule = q.rules[0]
        if (!rule) return false
        if (rule == "required") return true
        if (rule.includes("required_if_empty")) {
            const id = rule.split(":")[1]
            return !questions.find(q => q.questionId == id).answer
        }
        if (rule.includes("required_if")) {
            const [id, operator, value] = rule.split(":")[1].split(",")
            const answer = questions.find(q => q.questionId == id).answer
            switch (operator) {
                case "ne":
                    return answer != value
                default:
                    throw new Error(`Operator ${operator} is not implemented`)
            }
        }
    })
}

export const mutations = {
    "fundae/setAnswer"(state, { userActivityId, questionId, answer }) {
        if (!(userActivityId in state.userActivities)) {
            throw new Error(
                `The activity with id = ${userActivityId} is not defined`
            )
        }

        const activity = state.userActivities[userActivityId]
        if (!activity.state.answers) {
            activity.state.answers = []
        }
        const answers = activity.state.answers
        const answerIdx = answers.findIndex(a => a.questionId == questionId)
        if (answerIdx !== -1) {
            answers[answerIdx] = {
                answer,
                questionId,
            }
        } else {
            answers.push({
                answer,
                questionId,
            })
        }
    },
}

export function makeActions(service, i18n) {
    i18n = i18n || DEFAULT_I18N.global

    return {
        async "fundae/save"(
            { state, commit, dispatch },
            { userActivityId, questionId, answer }
        ) {
            if (!(userActivityId in state.userActivities)) {
                throw new Error(
                    `The activity with id = ${userActivityId} is not defined`
                )
            }

            try {
                const {
                    progress,
                    answeredRequiredQuestions,
                    totalRequiredQuestions,
                } = await service.save({ userActivityId, questionId, answer })

                commit("setState", {
                    userActivityId,
                    activityState: {
                        ...state.userActivities[userActivityId].state,
                        progress,
                        answeredRequiredQuestions,
                        totalRequiredQuestions,
                    },
                })
            } catch (error) {
                if (error instanceof PluginReviewActionNotAllowedError) {
                    dispatch("lms/refreshUserActivity", userActivityId, {
                        root: true,
                    })
                    return
                }
                throw error
            }
        },

        async "fundae/finish"({ state, commit, dispatch }, userActivityId) {
            if (!(userActivityId in state.userActivities)) {
                throw new Error(
                    `The activity with id = ${userActivityId} is not defined`
                )
            }

            try {
                const newState = await service.finish(userActivityId)

                commit("setState", {
                    userActivityId,
                    activityState: newState,
                })

                dispatch(
                    "pushNotification",
                    {
                        title: ucfirst(i18n.t("common.answersWereSaved", 2)),
                        type: "success",
                    },
                    { root: true }
                )
            } catch (error) {
                if (error instanceof PluginReviewActionNotAllowedError) {
                    dispatch("lms/refreshUserActivity", userActivityId, {
                        root: true,
                    })
                    return
                }
                dispatch(
                    "pushNotification",
                    {
                        title: error.message,
                        type: "error",
                    },
                    { root: true }
                )
            }
        },
    }
}

export default {
    mutations,
    makeActions,
}
